import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate, useLocation } from 'react-router-dom';
// form
import { useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import useThrottle from 'src/hooks/useThrottle';

// routes

import useLocales from 'src/hooks/useLocales';
// components

import { observer } from 'mobx-react-lite';
import { FormProvider, RHFSelect, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/Iconify';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useStore } from 'src/stores/store';
import { IShopInfo } from 'src/@types/Choqorak/MainTables/Shop/Shop';
import CustomRHFAutocomplete from 'src/components/hook-form/CustomRHFAutocomplete';
import uuid from 'react-uuid';

// ----------------------------------------------------------------------

export default observer(function ShopNewEditForm() {
  const { ShopStore, commonDropdown, ChoqorakCommonDropdown } = useStore();
  const { translate } = useLocales();
  const { createShop, updateShop, editMode, selectedShop, selectedShopDetail, clearSelectedShop } =
    ShopStore;
  const {
    loadElectricMetereDDL,
    ElectricMeterOption,
    loadFloorDDL,
    FloorOption,
    loadBlockeDDL,
    BlockOption,
    loadShopTypeDDL,
    ShopTypeOption,
    loadStatusDDL,
    StatusOption,
    loadCurrencyTypeDDL,
    CurrencyTypeOption,
    ElectricBoxOption,
    loadElectricBoxeDDL,
  } = ChoqorakCommonDropdown;
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const NewShopSchema = Yup.object().shape({
    floorId: Yup.string().required(`${translate('Validation.floorId')}`),
    blockId: Yup.string().required(`${translate('Validation.blockId')}`),
    shopCode: Yup.string().required(`${translate('Validation.shopCode')}`),
    shopStatusId: Yup.string().required(`${translate('Validation.shopStatusId')}`),
    shopTypeId: Yup.string().required(`${translate('Validation.shopTypeId')}`),
    shopWidth: Yup.string().required(`${translate('Validation.shopWidth')}`),
    shopLength: Yup.string().required(`${translate('Validation.shopLength')}`),
    rentPerMonth: Yup.string().required(`${translate('Validation.rentPerMonth')}`),
    rentCarrencyId: Yup.string().required(`${translate('Validation.rentCarrencyId')}`),
  });

  const defaultValues = useMemo<IShopInfo>(
    () => ({
      shopId: selectedShopDetail?.shopId,
      floorId: selectedShopDetail?.floorId || undefined,
      electricBoxId: selectedShopDetail?.electricBoxId || undefined,
      electricMeterId: selectedShopDetail?.electricMeterId || undefined,
      blockId: selectedShopDetail?.blockId || undefined,
      shopCode: selectedShopDetail?.shopCode || '',
      shopStatusId: selectedShopDetail?.shopStatusId || undefined,
      shopTypeId: selectedShopDetail?.shopTypeId || undefined,
      shopWidth: selectedShopDetail?.shopWidth || undefined,
      shopLength: selectedShopDetail?.shopLength || undefined,
      rentPerMonth: selectedShopDetail?.rentPerMonth || undefined,
      rentCarrencyId: selectedShopDetail?.rentCarrencyId || undefined,
      description: selectedShopDetail?.description || '',
    }),
    [selectedShopDetail]
  );

  const methods = useForm<IShopInfo>({
    resolver: yupResolver(NewShopSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    control,
    setValue,
    watch,
    setError,
    formState: { isSubmitting, errors },
  } = methods;
  const [ElectricMeterSerial, setElectricMeterSerial] = useState<string | undefined>('');
  // const electricMeterSerial = watch('electricMeterSerial');
  // const { control } = useFormContext();
  useEffect(() => {
    if (editMode) {
      reset(defaultValues);
    }
    if (!editMode) {
      reset(defaultValues);
    }
  }, [reset, editMode, defaultValues]);

  const onSubmit = useThrottle((data: IShopInfo) => {
    if (!editMode) {
      createShop(data)
        .then(() => {
          reset();
          enqueueSnackbar(`${translate('Tostar.CreateSuccess')}`);
          navigate(PATH_DASHBOARD.Shop.list);
        })
        .catch((err) => {
          var json = JSON.parse(err.request.response);
          if (json.error != null) {
            setError('afterSubmit', { ...err, message: json.message });
          }
        });
    } else {
      ///update
      updateShop(data)
        .then(() => {
          reset();
          enqueueSnackbar(`${translate('Tostar.UpdateSuccess')}`);
          navigate(PATH_DASHBOARD.Shop.list);
        })
        .catch((err) => {
          var json = JSON.parse(err.request.response);
          if (json.error != null) {
            setError('afterSubmit', { ...err, message: json.message });
          }
        });
    }
  });

  const val = watch();

  useEffect(() => {
    loadElectricBoxeDDL();

    loadFloorDDL();
    loadShopTypeDDL();
    loadBlockeDDL();
    loadStatusDDL(0);
    loadCurrencyTypeDDL();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (val.electricBoxId) {
      loadElectricMetereDDL(val.electricBoxId, false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [val.electricBoxId]);

  useEffect(() => {
    if (editMode) {
      if (defaultValues.electricBoxId) {
        loadElectricMetereDDL(val.electricBoxId!, true).then((res) => {
          setElectricMeterSerial(
            ElectricMeterOption.find((e) => e.value === defaultValues.electricMeterId)?.text
          );
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ElectricMeterSerial]);

  useEffect(() => {
    setValue('electricMeterSerial', ElectricMeterSerial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ElectricMeterSerial]);

  console.log(val.electricBoxId);

  const handleAutocompleteChange =
    (options: any[], valueKey: keyof IShopInfo, idKey: keyof IShopInfo) =>
    (_event: any, newValue: any) => {
      const selectedOption = options.find((item) => item.text === newValue);

      if (selectedOption) {
        setValue(idKey, Number(selectedOption.value));
        setValue(valueKey, selectedOption.text);
      } else {
        setValue(idKey, undefined);
        setValue(valueKey, '');
      }
    };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {!!errors.afterSubmit && (
        <Alert sx={{ mb: 2 }} severity="error">
          {errors.afterSubmit.message}
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Card sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFSelect
                name="floorId"
                label={translate('Shop.Floor')}
                size="medium"
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
                sx={{ maxWidth: { md: '100%' } }}
              >
                {FloorOption.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      mx: 1,
                      my: 0.5,
                      borderRadius: 0.75,
                      typography: 'body2',
                      textTransform: 'capitalize',
                    }}
                  >
                    {option.text}
                  </MenuItem>
                ))}
              </RHFSelect>

              <RHFTextField
                size="medium"
                name="shopCode"
                label={translate('Shop.ShopCode')}
                sx={{ maxWidth: { md: '100%' } }}
              />

              <RHFSelect
                name="blockId"
                label={translate('Shop.Block')}
                size="medium"
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
                sx={{ maxWidth: { md: '100%' } }}
              >
                {BlockOption.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      mx: 1,
                      my: 0.5,
                      borderRadius: 0.75,
                      typography: 'body2',
                      textTransform: 'capitalize',
                    }}
                  >
                    {option.text}
                  </MenuItem>
                ))}
              </RHFSelect>

              <RHFSelect
                name="electricBoxId"
                label={translate('Shop.ElectricBox')}
                size="medium"
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
                sx={{ maxWidth: { md: '100%' } }}
                onChange={(e) => {
                  setValue('electricBoxId', Number(e.target.value));
                }}
              >
                {ElectricBoxOption.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      mx: 1,
                      my: 0.5,
                      borderRadius: 0.75,
                      typography: 'body2',
                      textTransform: 'capitalize',
                    }}
                  >
                    {option.text}
                  </MenuItem>
                ))}
              </RHFSelect>

              <CustomRHFAutocomplete
                name="electricMeterSerial"
                label={translate('Shop.ElectricMeter')}
                showAsterisk
                placeholder={translate('Shop.ElectricMeter')}
                value={watch('electricMeterSerial') || ''}
                options={ElectricMeterOption.map((option) => option.text)}
                getOptionLabel={(option) => option}
                onChange={handleAutocompleteChange(
                  ElectricMeterOption,
                  'electricMeterSerial',
                  'electricMeterId'
                )}
                fullWidth
                renderOption={(props, option) => (
                  <li {...props} key={`${option}-${uuid()}`}>
                    {option}
                  </li>
                )}
              />

              <RHFSelect
                name="shopStatusId"
                label={translate('Shop.ShopStatus')}
                size="medium"
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
                sx={{ maxWidth: { md: '100%' } }}
              >
                {StatusOption.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      mx: 1,
                      my: 0.5,
                      borderRadius: 0.75,
                      typography: 'body2',
                      textTransform: 'capitalize',
                    }}
                  >
                    {option.text}
                  </MenuItem>
                ))}
              </RHFSelect>

              <RHFSelect
                name="shopTypeId"
                label={translate('Shop.ShopType')}
                size="medium"
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
                sx={{ maxWidth: { md: '100%' } }}
              >
                {ShopTypeOption.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      mx: 1,
                      my: 0.5,
                      borderRadius: 0.75,
                      typography: 'body2',
                      textTransform: 'capitalize',
                    }}
                  >
                    {option.text}
                  </MenuItem>
                ))}
              </RHFSelect>

              <RHFTextField
                size="medium"
                type="number"
                name="shopWidth"
                label={translate('Shop.ShopWidth')}
                sx={{ maxWidth: { md: '100%' } }}
              />

              <RHFTextField
                size="medium"
                type="number"
                name="shopLength"
                label={translate('Shop.ShopLength')}
                sx={{ maxWidth: { md: '100%' } }}
              />

              <RHFSelect
                name="rentCarrencyId"
                label={translate('Shop.RentCurrency')}
                size="medium"
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
                sx={{ maxWidth: { md: '100%' } }}
              >
                {CurrencyTypeOption.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      mx: 1,
                      my: 0.5,
                      borderRadius: 0.75,
                      typography: 'body2',
                      textTransform: 'capitalize',
                    }}
                  >
                    {option.text}
                  </MenuItem>
                ))}
              </RHFSelect>

              <RHFTextField
                size="medium"
                type="number"
                name="rentPerMonth"
                label={translate('Shop.RentPerMonth')}
                sx={{ maxWidth: { md: '100%' } }}
              />

              <RHFTextField
                size="medium"
                name="description"
                label={translate('Shop.Description')}
                sx={{ maxWidth: { md: '100%' } }}
              />
            </Box>

            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
              <LoadingButton
                fullWidth
                type="submit"
                variant="contained"
                loading={isSubmitting}
                startIcon={
                  !editMode ? <Iconify icon="eva:plus-fill" /> : <Iconify icon="eva:edit-fill" />
                }
              >
                {!editMode ? `${translate('CRUD.Save')}` : `${translate('CRUD.Update')}`}
              </LoadingButton>
              <Button
                fullWidth
                variant="contained"
                color="error"
                startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
                onClick={() => {
                  clearSelectedShop();
                  navigate(PATH_DASHBOARD.Shop.list);
                }}
              >
                {translate('CRUD.BackToList')}
              </Button>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
});
